import { useCallback, useEffect, useRef, useState } from 'react'
import { fetchChatActivity, resolveActivityApiUrl } from '../api/activity'
import type { ChatActivity } from '../api/activity'

type LoadState = { key: string; data: ChatActivity | null; loading: boolean; error: string | null }

export function useChatActivity(from: number, to: number) {
  let endpoint: string | null = null
  let configurationError: string | null = null
  try {
    endpoint = resolveActivityApiUrl(import.meta.env.VITE_CHAT_ACTIVITY_API_URL, window.location.origin)
  } catch {
    configurationError = 'Configure VITE_CHAT_ACTIVITY_API_URL with a valid HTTP(S) endpoint'
  }
  const key = `${endpoint}:${from}:${to}`
  const [state, setState] = useState<LoadState | null>(null)
  const [revision, setRevision] = useState(0)
  const previousRequest = useRef<{ key: string; revision: number } | null>(null)

  useEffect(() => {
    if (!endpoint) return
    const controller = new AbortController()
    let active = true
    const refresh = previousRequest.current?.key === key && previousRequest.current.revision !== revision
    previousRequest.current = { key, revision }
    setState((previous) => ({ key, data: previous?.key === key ? previous.data : null, loading: true, error: null }))
    void fetchChatActivity(endpoint, { from, to }, controller.signal, refresh)
      .then((data) => {
        if (active) setState({ key, data, loading: false, error: null })
      })
      .catch((error: unknown) => {
        if (!active) return
        setState((previous) => ({
          key,
          data: previous?.key === key ? previous.data : null,
          loading: false,
          error: error instanceof Error ? error.message : 'Could not load chat activity',
        }))
      })
    return () => {
      active = false
      controller.abort()
    }
  }, [endpoint, key, from, to, revision])

  const reload = useCallback(() => setRevision((value) => value + 1), [])
  const current = state?.key === key ? state : null
  return {
    configured: endpoint !== null,
    data: endpoint ? current?.data ?? null : null,
    loading: endpoint !== null && (current?.loading ?? true),
    error: configurationError ?? (endpoint ? current?.error ?? null : null),
    reload,
  }
}
